import { memo, useEffect, useRef, useState } from 'react'
import { profileImageCache } from '@/lib/services/profile-image-cache.service'
import { cn } from '@/lib/utils'
import { CircleNotchIcon } from '@phosphor-icons/react'

import { Avatar, AvatarFallback, AvatarImage } from './avatar'

interface User {
  id?: string | null
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  displayName?: string | null
  name?: string | null
}

interface UserAvatarProps {
  user?: User | null
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  className?: string
  showLoading?: boolean
}

const sizeClasses = {
  xs: 'h-5 w-5',
  sm: 'h-6 w-6',
  md: 'h-8 w-8',
  lg: 'h-10 w-10',
  xl: 'h-12 w-12',
  '2xl': 'h-16 w-16',
}

const fontSizeClasses = {
  xs: 'text-[8px]',
  sm: 'text-[9px]',
  md: 'text-[11px]',
  lg: 'text-xs',
  xl: 'text-sm',
  '2xl': 'text-base',
}

// Image cache to prevent flashing between users
const imageUrlCache = new Map<string, string | null>()

export const invalidateUserAvatarImage = (userId: string): void => {
  imageUrlCache.delete(userId)

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('scrambl:user-avatar-updated', { detail: { userId } }),
    )
  }
}

/**
 * UserAvatar - A lightweight component for displaying user avatars
 *
 * This component is optimized for display-only scenarios like task assignees,
 * comment authors, etc. Uses improved caching to prevent flashing between images.
 */
export const UserAvatar = memo(
  function UserAvatar({
    user,
    size = 'md',
    className,
    showLoading = false,
  }: UserAvatarProps) {
    const [imageUrl, setImageUrl] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [hasError, setHasError] = useState(false)

    // Track the current user ID to prevent stale updates
    const currentUserIdRef = useRef<string | null>(null)

    // Initials computed each render (React Compiler handles memoization)
    const initials = (() => {
      if (user?.displayName) {
        return user.displayName
          .split(' ')
          .map(name => name.charAt(0))
          .join('')
          .toUpperCase()
          .slice(0, 2)
      } else if (user?.firstName && user?.lastName) {
        return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
      } else if (user?.name) {
        return user.name
          .split(' ')
          .map(name => name.charAt(0))
          .join('')
          .toUpperCase()
          .slice(0, 2)
      } else if (user?.email) {
        return user.email.charAt(0).toUpperCase()
      }
      return '?'
    })()

    // Improved image loading with anti-flashing logic
    useEffect(() => {
      if (!user?.id) {
        // Clear state if no user
        setImageUrl(null)
        setIsLoading(false)
        setHasError(false)
        currentUserIdRef.current = null
        return
      }

      const userId = user.id

      // Update current user reference
      currentUserIdRef.current = userId

      // Check our local cache first to prevent flashing
      const cachedUrl = imageUrlCache.get(userId)
      if (cachedUrl !== undefined) {
        if (currentUserIdRef.current === userId) {
          setImageUrl(cachedUrl)
          setIsLoading(false)
          setHasError(false)
        }
        // Even if cached, still try to get fresh data in background
        loadImageInBackground(userId)
        return
      }

      // No cache hit - load image (but smartly manage loading state)
      loadImage(userId, true)
    }, [user?.id])

    const loadImageInBackground = async (userId: string) => {
      try {
        const url = await profileImageCache.getProfileImage(userId)

        // Update cache
        imageUrlCache.set(userId, url)

        // Only update state if this is still the current user
        if (currentUserIdRef.current === userId) {
          setImageUrl(url)
          setHasError(false)
        }
      } catch (error) {
        imageUrlCache.set(userId, null)
      }
    }

    const loadImage = async (
      userId: string,
      showLoadingState: boolean = false,
      attempt: number = 0,
    ) => {
      // Only show loading state on initial load, not on subsequent user changes
      if (showLoadingState && showLoading && attempt === 0) {
        setIsLoading(true)
      }

      setHasError(false)

      try {
        const url = await profileImageCache.getProfileImage(userId)

        // Update our local cache
        imageUrlCache.set(userId, url)

        // Only update state if this is still the current user
        if (currentUserIdRef.current === userId) {
          setImageUrl(url)
          setHasError(false)
          setIsLoading(false)
        }
      } catch (error) {
        // Cache the failure too
        imageUrlCache.set(userId, null)

        if (currentUserIdRef.current === userId) {
          // Retry silently without showing error state
          if (attempt < 1) {
            setTimeout(() => {
              if (currentUserIdRef.current === userId) {
                loadImage(userId, false, attempt + 1)
              }
            }, 200)
          } else {
            setHasError(true)
            setImageUrl(null)
            setIsLoading(false)
          }
        }
      }
    }

    useEffect(() => {
      if (!user?.id || typeof window === 'undefined') {
        return
      }

      const handleAvatarUpdate = (event: Event) => {
        const updatedUserId = (event as CustomEvent<{ userId?: string }>).detail
          ?.userId
        if (updatedUserId === user.id) {
          void loadImage(updatedUserId, false)
        }
      }

      window.addEventListener('scrambl:user-avatar-updated', handleAvatarUpdate)

      return () => {
        window.removeEventListener(
          'scrambl:user-avatar-updated',
          handleAvatarUpdate,
        )
      }
    }, [user?.id])

    return (
      <Avatar className={cn(sizeClasses[size], className)}>
        {!isLoading && imageUrl && !hasError ? (
          <AvatarImage
            src={imageUrl}
            alt={
              user?.displayName || user?.name || user?.firstName || 'User'
            }
            loading="lazy"
            onError={() => {
              // Handle image load errors gracefully and update cache
              const currentUserId = currentUserIdRef.current
              if (currentUserId) {
                imageUrlCache.set(currentUserId, null)
              }
              setHasError(true)
              setImageUrl(null)
            }}
          />
        ) : null}
        <AvatarFallback
          className={cn('font-semibold text-white', fontSizeClasses[size])}
          identifier={
            user?.id ||
            user?.email ||
            user?.displayName ||
            user?.name ||
            initials
          }
        >
          {isLoading && showLoading ? (
            <CircleNotchIcon className="h-4 w-4 animate-spin" />
          ) : (
            initials
          )}
        </AvatarFallback>
      </Avatar>
    )
  },
  (prevProps, nextProps) => {
    // Deep comparison to prevent unnecessary re-renders during avatar flashing
    return (
      prevProps.user?.id === nextProps.user?.id &&
      prevProps.user?.displayName === nextProps.user?.displayName &&
      prevProps.user?.name === nextProps.user?.name &&
      prevProps.user?.firstName === nextProps.user?.firstName &&
      prevProps.user?.lastName === nextProps.user?.lastName &&
      prevProps.user?.email === nextProps.user?.email &&
      prevProps.size === nextProps.size &&
      prevProps.className === nextProps.className &&
      prevProps.showLoading === nextProps.showLoading
    )
  },
)
